import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Track } from '../trackList/track';
import './TrackInfoModal.css';

interface Props {
    track: Track;
    onClose: () => void;
}

function formatDuration(ms: number): string {
    const totalSeconds = Math.round(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
}

export function TrackInfoModal({ track, onClose }: Props): React.JSX.Element {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const rows: [string, string | undefined][] = [
        ['Title', track.getTitle()],
        ['Artist', track.getArtist()],
        ['Album', track.getAlbum()],
        ['Track number', track.getTrackNumber()?.toString()],
        ['Duration', track.getDuration() ? formatDuration(track.getDuration()) : undefined],
        ['MusicBrainz ID', track.getMbid()],
    ];

    return createPortal(
        <div
            className="trackInfoOverlay"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="trackInfoPanel">
                <button className="trackInfoClose" onClick={onClose} aria-label="Close track info">
                    <X size={16} strokeWidth={2.75} />
                </button>
                <h2 className="trackInfoTitle">Track info</h2>
                <div className="trackInfoRows">
                    {rows
                        .filter(([, value]) => Boolean(value))
                        .map(([label, value]) => (
                            <div className="trackInfoRow" key={label}>
                                <span className="trackInfoLabel">{label}</span>
                                <span className="trackInfoValue">{value}</span>
                            </div>
                        ))}
                </div>
            </div>
        </div>,
        document.body
    );
}
